import { ApiError } from '@/shared/api/ApiError'

/**
 * Best-effort human-readable message for anything thrown by the data layer.
 *
 * Returns an empty string when nothing useful can be extracted, so callers can
 * fall back to their own copy rather than rendering "[object Object]".
 */
export function getErrorMessage(error: unknown): string {
  if (!error) return ''
  if (error instanceof ApiError || error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return ''
}

/** HTTP status of an API failure, when the error came from our Axios client. */
export function getErrorStatus(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined
}

/** The API's `x-correlation-id`, so users can quote it in a ticket. */
export function getCorrelationId(error: unknown): string | undefined {
  return error instanceof ApiError ? error.correlationId : undefined
}

/** True when the failure was a missing route — used for back-end feature detection. */
export function isNotFound(error: unknown): boolean {
  return getErrorStatus(error) === 404
}

/** `details[].path` → message, for showing API validation next to fields. Nested paths keep their prefix (`rate.feePerPeriod`). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {}
  return Object.fromEntries((error.details ?? []).map((d) => [d.path, d.message]))
}
